export type ProductionSpan = 'months' | 'year' | 'quarter';

export interface ProductionPoint {
  period: string;
  distanceKm: number;
  costKr: number;
}

export function monthKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}`;
}

export function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Tolv månader bakåt, från den första dagen i den äldsta månaden. */
export function rollingYearRange(today: Date): { startDate: string; endDate: string } {
  const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  return { startDate: localIsoDate(start), endDate: localIsoDate(today) };
}

export function monthKeysForSpan(span: ProductionSpan, today: Date): string[] {
  if (span === 'year') {
    const keys: string[] = [];
    for (let month = 0; month <= today.getMonth(); month += 1) {
      keys.push(monthKey(new Date(today.getFullYear(), month, 1)));
    }
    return keys;
  }
  const keys: string[] = [];
  for (let offset = 11; offset >= 0; offset -= 1) {
    keys.push(monthKey(new Date(today.getFullYear(), today.getMonth() - offset, 1)));
  }
  return keys;
}

export function pointsForKeys(keys: string[], rows: ProductionPoint[]): ProductionPoint[] {
  const byPeriod = new Map(rows.map((row) => [row.period, row]));
  return keys.map((period) => byPeriod.get(period) ?? { period, distanceKm: 0, costKr: 0 });
}

export function groupByQuarter(points: ProductionPoint[]): ProductionPoint[] {
  const groups = new Map<string, ProductionPoint>();
  for (const point of points) {
    const [yearText, monthText] = point.period.split('-');
    const quarter = Math.floor((Number(monthText) - 1) / 3) + 1;
    const period = `${yearText}-Q${quarter}`;
    const current = groups.get(period) ?? { period, distanceKm: 0, costKr: 0 };
    current.distanceKm += point.distanceKm;
    current.costKr += point.costKr;
    groups.set(period, current);
  }
  return [...groups.values()];
}

export function seriesForSpan(span: ProductionSpan, today: Date, rows: ProductionPoint[]): ProductionPoint[] {
  const monthly = pointsForKeys(monthKeysForSpan(span === 'year' ? 'year' : 'months', today), rows);
  if (span === 'quarter') return groupByQuarter(monthly);
  return monthly;
}

export function percentChange(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

export function formatCompactAmount(value: number, unit: 'km' | 'kr'): string {
  if (value >= 1_000_000) {
    const text = (value / 1_000_000).toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return unit === 'km' ? `${text} Mkm` : `${text} Mkr`;
  }
  const text = Math.round(value).toLocaleString('sv-SE');
  return unit === 'km' ? `${text} km` : `${text} kr`;
}

export function formatKrPerKm(value: number): string {
  return `${value.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr/km`;
}

export interface ChartScale {
  min: number;
  max: number;
  ticks: number[];
}

function niceStep(span: number): number {
  if (span <= 0) return 1;
  const exponent = Math.floor(Math.log10(span));
  const fraction = span / 10 ** exponent;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return nice * 10 ** exponent;
}

function clean(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** Tre skalstreck. Noll i botten, utom när alla värden ligger högt och axeln kan zoomas. */
export function chartScale(values: number[], zeroBaseline: boolean): ChartScale {
  const dataMax = Math.max(0, ...values);
  const dataMin = values.length ? Math.min(...values) : 0;
  if (dataMax <= 0) return { min: 0, max: 0, ticks: [0] };

  if (zeroBaseline || dataMin <= 0) {
    const step = Math.max(1, niceStep(dataMax / 2));
    const max = clean(Math.ceil(dataMax / step) * step);
    return { min: 0, max, ticks: [max, clean(max / 2), 0] };
  }

  const pad = (dataMax - dataMin) * 0.25 || dataMax * 0.1;
  const step = Math.max(1, niceStep((dataMax - dataMin + pad * 2) / 2));
  const max = clean(Math.ceil((dataMax + pad) / step) * step);
  const min = clean(Math.max(0, Math.floor((dataMin - pad) / step) * step));
  return { min, max, ticks: [max, clean(min + (max - min) / 2), min] };
}

export function formatAxisTick(value: number): string {
  if (value === 0) return '0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const millions = value / 1_000_000;
    const digits = Number.isInteger(millions) ? 0 : 1;
    const text = millions.toLocaleString('sv-SE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return `${text} M`;
  }
  if (!Number.isInteger(value)) {
    return value.toLocaleString('sv-SE', { maximumFractionDigits: 1 });
  }
  return Math.round(value).toLocaleString('sv-SE');
}
